import React, { useState, useEffect, useCallback } from "react";
import api from "../api/client";
import { AppContext, useApp } from "./useApp";
import { initPush, unregisterPush, showSystemNotification } from "../push/registerPush";

export { useApp };

export function AppProvider({ children }) {
  const [currentUser, setCurrentUserState] = useState(() => {
    try {
      const token = sessionStorage.getItem("fp_token");
      const savedUser = sessionStorage.getItem("fp_user");
      if (token && savedUser) {
        return JSON.parse(savedUser);
      }
    } catch (e) {
      console.error("Failed to restore session from sessionStorage:", e);
    }
    return null;
  });
  const [employee, setEmployee] = useState(null);   // Full profile (employee portal)
  const [team, setTeam] = useState([]);             // All employees (manager portal)
  const [tasks, setTasks] = useState([]);           // Tasks for current employee
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Notifications State ──────────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const seenNotifIdsRef = React.useRef(new Set());

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      if (res.data && res.data.data) {
        const notifList = res.data.data;
        setNotifications(notifList);
        setUnreadNotifCount(res.data.unreadCount || 0);

        // Check for new unread notifications that haven't been alerted as system banners yet
        notifList.forEach(n => {
          if (!n.is_read && !seenNotifIdsRef.current.has(n.id)) {
            seenNotifIdsRef.current.add(n.id);
            // Trigger System Background Notification Banner
            showSystemNotification(n.title, { body: n.body });
          } else {
            seenNotifIdsRef.current.add(n.id);
          }
        });
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  }, []);

  const markNotificationsRead = useCallback(async (notifId = null) => {
    try {
      await api.patch("/notifications/read", { id: notifId });
      await fetchNotifications();
    } catch (err) {
      console.error("Error marking notifications read:", err);
    }
  }, [fetchNotifications]);

  // ── Auth ───────────────────────────────────────────────────────────────────
  const login = useCallback(async (employeeId, password, gpsLocation = null) => {
    setLoading(true); setError(null);
    try {
      const payload = { employeeId, password, ...(gpsLocation || {}) };
      const { data } = await api.post("/auth/login", payload);
      sessionStorage.setItem("fp_token", data.token);
      sessionStorage.setItem("fp_user", JSON.stringify(data.user));
      setCurrentUserState(data.user);
      return data.user;
    } catch (err) {
      const msg = err.response?.data?.error || "Login failed";
      setError(msg); throw new Error(msg);
    } finally { setLoading(false); }
  }, []);

  const setupPassword = useCallback(async (employeeId, password) => {
    setLoading(true); setError(null);
    try {
      await api.post("/auth/setup-password", { employeeId, password });
    } catch (err) {
      const msg = err.response?.data?.error || "Setup failed";
      setError(msg); throw new Error(msg);
    } finally { setLoading(false); }
  }, []);

  const logout = useCallback(() => {
    // Clear user immediately to stop background syncs and loops
    setCurrentUserState(null);
    unregisterPush().finally(() => {
      sessionStorage.removeItem("fp_token");
      sessionStorage.removeItem("fp_user");
      setEmployee(null); setTeam([]); setTasks([]); setNotifications([]); setUnreadNotifCount(0);
    });
  }, []);

  // ── Load data after login ──────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    
    // Fetch notifications once on login
    fetchNotifications();
    
    if (currentUser.role === "employee") {
      if (!employee && !loading && !error) {
        loadEmployeeData(currentUser.employeeId || currentUser.id);
      }
    } else {
      // Manager
      if (team.length === 0 && !loading && !error) {
        loadTeamData();
      }
    }
  }, [currentUser, employee, loading, error, team.length]);

  // ── Push notifications: register this device once signed in ────────────────
  useEffect(() => {
    if (!currentUser) return;
    initPush();
  }, [currentUser]);

  // ── Periodic Silent Background Sync & Notification Polling ─────────────────
  useEffect(() => {
    if (!currentUser) return;
    const interval = setInterval(async () => {
      try {
        if (currentUser.role === "employee") {
          const [empRes, taskRes] = await Promise.all([
            api.get(`/employees/${currentUser.employeeId}`),
            api.get("/tasks"),
          ]);
          setEmployee(empRes.data.data);
          setTasks(taskRes.data.data);
        } else {
          const { data } = await api.get(`/employees?_t=${Date.now()}`);
          setTeam(data.data);
        }
        await fetchNotifications();
      } catch (err) {
        console.error("Background sync error:", err);
      }
    }, 4000); // Sync every 4 seconds silently

    return () => clearInterval(interval);
  }, [currentUser, fetchNotifications]);

  const loadEmployeeData = useCallback(async (empId) => {
    if (!empId) {
      logout();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [empRes, taskRes] = await Promise.all([
        api.get(`/employees/${empId}`),
        api.get("/tasks"),
      ]);
      setEmployee(empRes.data.data);
      setTasks(taskRes.data.data);
    } catch (err) {
      console.error("Load employee error:", err);
      if (err.response?.status === 401) {
        setError("Session expired");
        logout();
      } else {
        setError("Failed to load profile data");
      }
    } finally { setLoading(false); }
  }, [logout]);

  const loadTeamData = useCallback(async () => {
    if (currentUser?.role !== "manager" && currentUser?.role !== "superadmin") {
      setTeam([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/employees?_t=${Date.now()}`);
      setTeam(data.data || []);
    } catch (err) { 
      console.error("Load team error:", err); 
      if (err.response?.status === 401) {
        setError("Session expired");
        logout();
      } else {
        setError("Failed to load team data");
      }
    }
    finally { setLoading(false); }
  }, [currentUser, logout]);

  const refreshEmployee = useCallback(() => {
    if (currentUser?.role === "employee") loadEmployeeData(currentUser.employeeId);
  }, [currentUser, loadEmployeeData]);

  const refreshTeam = useCallback(() => {
    if (currentUser?.role === "manager" || currentUser?.role === "superadmin") {
      return loadTeamData();
    }
  }, [currentUser, loadTeamData]);

  // ── Employee actions ───────────────────────────────────────────────────────
  const getDeviceTime = () => new Date().toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true });
  const getDeviceFullTime = () => `${getDeviceTime()}, ${new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short" })}`;

  const doCheckIn = useCallback(async (location) => {
    const checkInTime = getDeviceTime();
    await api.post("/checkin", {
      lat: location.lat,
      lng: location.lng,
      accuracy: location.accuracy,
      city: location.city,
      isRealGps: location.real,
      locationDenied: location.locationDenied || false,
      checkInTime,
      deviceTime: checkInTime
    });
    await loadEmployeeData(currentUser.employeeId);
    await refreshTeam();
  }, [currentUser, loadEmployeeData, refreshTeam]);

  const doCheckOut = useCallback(async (location) => {
    const checkOutTime = getDeviceTime();
    await api.post("/checkin/out", {
      checkOutTime,
      deviceTime: checkOutTime,
      lat: location?.lat,
      lng: location?.lng,
      accuracy: location?.accuracy,
      city: location?.city,
      isRealGps: location?.real,
      locationDenied: location?.locationDenied || false
    });
    await loadEmployeeData(currentUser.employeeId);
    await refreshTeam();
  }, [currentUser, loadEmployeeData, refreshTeam]);

  const declareOD = useCallback(async (record) => {
    await api.post("/od", record);
    await loadEmployeeData(currentUser.employeeId);
    await refreshTeam();
  }, [currentUser, loadEmployeeData, refreshTeam]);

  const markODArrived = useCallback(async (odId, location) => {
    const deviceTime = getDeviceTime();
    await api.patch(`/od/${odId}/arrive`, { lat: location.lat, lng: location.lng, city: location.city, deviceTime, arrivalTime: deviceTime });
    await loadEmployeeData(currentUser.employeeId);
    await refreshTeam();
  }, [currentUser, loadEmployeeData, refreshTeam]);

  const completeOD = useCallback(async (odId) => {
    const deviceTime = getDeviceFullTime();
    await api.patch(`/od/${odId}/complete`, { deviceTime, completedTime: deviceTime });
    await loadEmployeeData(currentUser.employeeId);
    await refreshTeam();
  }, [currentUser, loadEmployeeData, refreshTeam]);

  const completeTask = useCallback(async (taskId, location, report) => {
    const deviceTime = getDeviceTime();
    const photoFile = report?.photo || report?.taskPhoto;
    if (photoFile instanceof File || photoFile instanceof Blob) {
      const formData = new FormData();
      formData.append("lat", location?.lat || "");
      formData.append("lng", location?.lng || "");
      formData.append("status", report?.status || "Completed");
      formData.append("team", report?.team || "general");
      formData.append("remarks", report?.remarks || "");
      formData.append("deviceTime", deviceTime);
      formData.append("photo", photoFile);
      await api.patch(`/tasks/${taskId}/complete`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    } else {
      await api.patch(`/tasks/${taskId}/complete`, {
        lat: location?.lat,
        lng: location?.lng,
        status: report?.status,
        team: report?.team,
        remarks: report?.remarks,
        deviceTime
      });
    }
    await loadEmployeeData(currentUser.employeeId);
  }, [currentUser, loadEmployeeData]);

  const addReimbursement = useCallback(async (request) => {
    const { receipt, ...body } = request;
    const { data } = await api.post("/reimbursements", body);
    if (receipt) {
      try {
        const res = await fetch(receipt);
        const blob = await res.blob();
        const formData = new FormData();
        formData.append("receipt", blob, "receipt.jpg");
        await api.post(`/reimbursements/${data.data.id}/receipt`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      } catch (err) {
        console.error("Receipt upload failed:", err);
      }
    }
    await loadEmployeeData(currentUser.employeeId);
  }, [currentUser, loadEmployeeData]);

  const addIncident = useCallback(async (request) => {
    const { photo, ...body } = request;
    const { data } = await api.post("/incidents", body);
    if (photo) {
      try {
        const res = await fetch(photo);
        const blob = await res.blob();
        const formData = new FormData();
        formData.append("photo", blob, "photo.jpg");
        await api.post(`/incidents/${data.data.id}/photo`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      } catch (err) {
        console.error("Incident photo upload failed:", err);
      }
    }
    await loadEmployeeData(currentUser.employeeId);
  }, [currentUser, loadEmployeeData]);

  const editIncident = useCallback(async (incidentId, request) => {
    const { photo, ...body } = request;
    await api.put(`/incidents/${incidentId}`, body);
    if (photo) {
      try {
        const res = await fetch(photo);
        const blob = await res.blob();
        const formData = new FormData();
        formData.append("photo", blob, "photo.jpg");
        await api.post(`/incidents/${incidentId}/photo`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      } catch (err) {
        console.error("Incident photo upload failed:", err);
      }
    }
    if (currentUser?.role === "employee") {
      await loadEmployeeData(currentUser.employeeId);
    } else {
      await loadTeamData();
    }
  }, [currentUser, loadEmployeeData, loadTeamData]);

  const deleteIncident = useCallback(async (incidentId) => {
    await api.delete(`/incidents/${incidentId}`);
    if (currentUser?.role === "employee") {
      await loadEmployeeData(currentUser.employeeId);
    } else {
      await loadTeamData();
    }
  }, [currentUser, loadEmployeeData, loadTeamData]);

  const updateProfile = useCallback(async (field, fileOrValue) => {
    if (typeof field === "object") {
      await api.patch(`/employees/${currentUser.employeeId}`, field);
      await refreshTeam();
    } else if (field === "aadhaarFront" || field === "aadhaarBack") {
      const formData = new FormData();
      let fileObj = fileOrValue;
      if (typeof fileOrValue === "string" && fileOrValue.startsWith("data:")) {
        const res = await fetch(fileOrValue);
        fileObj = await res.blob();
      }
      formData.append("file", fileObj, "aadhaar.jpg");
      formData.append("side", field === "aadhaarFront" ? "front" : "back");
      await api.post(`/employees/${currentUser.employeeId}/aadhaar`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      await refreshTeam();
    } else if (field === "selfie") {
      const formData = new FormData();
      let fileObj = fileOrValue;
      if (typeof fileOrValue === "string" && fileOrValue.startsWith("data:")) {
        const res = await fetch(fileOrValue);
        fileObj = await res.blob();
      }
      formData.append("selfie", fileObj, "selfie.jpg");
      await api.post(`/employees/${currentUser.employeeId}/selfie`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      await refreshTeam();
    } else {
      await api.patch(`/employees/${currentUser.employeeId}`, { [field]: fileOrValue });
      await refreshTeam();
    }
    await loadEmployeeData(currentUser.employeeId);
  }, [currentUser, loadEmployeeData, refreshTeam]);

  // ── Manager actions ───────────────────────────────────────────────────────
  const assignTask = useCallback(async (taskData) => {
    await api.post("/tasks", taskData);
    await refreshTeam();
  }, [refreshTeam]);

  const addEmployee = useCallback(async (empData) => {
    await api.post("/employees", empData);
    await refreshTeam();
  }, [refreshTeam]);

  const updateReimbursement = useCallback(async (empId, reimId, status, rejectReason, paymentDate, paymentTime) => {
    await api.patch(`/reimbursements/${reimId}`, { status, rejectReason, paymentDate, paymentTime });
    await loadTeamData();
  }, [loadTeamData]);

  const resetEmployeePassword = useCallback(async (empId, password) => {
    await api.patch(`/employees/${empId}/reset-password`, { password });
  }, []);

  // ── Leave actions ────────────────────────────────────────────────────────────
  const applyLeave = useCallback(async (leaveData) => {
    const res = await api.post("/leaves", leaveData);
    if (currentUser?.role === "employee") await loadEmployeeData(currentUser.employeeId);
    await refreshTeam();
    return res.data;
  }, [currentUser, loadEmployeeData, refreshTeam]);

  const fetchLeaves = useCallback(async () => {
    const res = await api.get("/leaves");
    return res.data;
  }, []);

  const updateLeave = useCallback(async (leaveId, status, rejectReason) => {
    await api.patch(`/leaves/${leaveId}`, { status, rejectReason });
    await loadTeamData();
  }, [loadTeamData]);

  // ── Lunch actions ────────────────────────────────────────────────────────────
  const requestLunch = useCallback(async () => {
    const res = await api.post("/lunch");
    return res.data;
  }, []);

  const fetchMyLunchRequest = useCallback(async () => {
    const res = await api.get("/lunch/my");
    return res.data;
  }, []);

  const fetchTodayLunchRequests = useCallback(async () => {
    const res = await api.get("/lunch/today");
    return res.data;
  }, []);

  const updateLunchRequest = useCallback(async (id, status) => {
    const res = await api.put(`/lunch/${id}/status`, { status });
    return res.data;
  }, []);

  const updateDailyReport = useCallback(async (reportId, reportData) => {
    let res;
    if (reportData.reportPhoto instanceof File) {
      const formData = new FormData();
      formData.append("date", reportData.date || "");
      formData.append("work", reportData.work || "");
      formData.append("timeSpent", reportData.timeSpent || "");
      formData.append("hours", reportData.hours || 0);
      formData.append("remarks", reportData.remarks || "");
      formData.append("reportPhoto", reportData.reportPhoto);
      res = await api.put(`/reports/${reportId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    } else {
      res = await api.put(`/reports/${reportId}`, reportData);
    }
    if (currentUser?.role === "employee") {
      await loadEmployeeData(currentUser.employeeId);
    }
    await refreshTeam();
    return res.data;
  }, [currentUser, loadEmployeeData, refreshTeam]);

  const submitDailyReport = useCallback(async (reportData) => {
    let res;
    if (reportData.reportPhoto instanceof File) {
      const formData = new FormData();
      formData.append("date", reportData.date || "");
      formData.append("work", reportData.work || "");
      formData.append("timeSpent", reportData.timeSpent || "");
      formData.append("hours", reportData.hours || 0);
      formData.append("remarks", reportData.remarks || "");
      formData.append("reportPhoto", reportData.reportPhoto);
      res = await api.post("/reports", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    } else {
      res = await api.post("/reports", reportData);
    }
    if (currentUser?.role === "employee") {
      await loadEmployeeData(currentUser.employeeId);
    }
    await refreshTeam();
    return res.data;
  }, [currentUser, loadEmployeeData, refreshTeam]);

  const fetchMyDailyReports = useCallback(async () => {
    const res = await api.get("/reports/my");
    return res.data;
  }, []);

  const fetchAllDailyReports = useCallback(async (params = {}) => {
    const res = await api.get("/reports", { params });
    return res.data;
  }, []);

  const fetchCalendarSummary = useCallback(async (month) => {
    const res = await api.get("/reports/calendar-summary", { params: { month } });
    return res.data;
  }, []);

  const getEmployee = useCallback((id) => team.find(e => e.id === id || e.employee_id === id), [team]);

  const rateTask = useCallback(async (taskId, rating, feedback) => {
    await api.patch(`/tasks/${taskId}/rate`, { rating, feedback });
    await loadTeamData();
  }, [loadTeamData]);

  const approveTask = useCallback(async (taskId) => {
    await api.patch(`/tasks/${taskId}/approve`);
    await loadTeamData();
  }, [loadTeamData]);

  const rejectTask = useCallback(async (taskId, reason) => {
    await api.patch(`/tasks/${taskId}/reject`, { reason });
    await loadTeamData();
  }, [loadTeamData]);

  const markEmployeeAbsent = useCallback(async (employeeId) => {
    await api.post(`/attendance/${employeeId}/absent`);
    await refreshTeam();
  }, [refreshTeam]);

  const value = {
    currentUser, employee, team, tasks, loading, error,
    notifications, unreadNotifCount, fetchNotifications, markNotificationsRead,
    login, logout, setupPassword,
    getEmployee,
    doCheckIn, doCheckOut,
    declareOD, markODArrived, completeOD,
    completeTask, rateTask, approveTask, rejectTask,
    assignTask, addEmployee,
    addReimbursement, updateReimbursement,
    addIncident, editIncident, deleteIncident,
    updateProfile,
    refreshEmployee, refreshTeam,
    markEmployeeAbsent,
    resetEmployeePassword,
    submitDailyReport, fetchMyDailyReports, fetchAllDailyReports, fetchCalendarSummary,
    applyLeave, fetchLeaves, updateLeave,
    requestLunch, fetchMyLunchRequest, fetchTodayLunchRequests, updateLunchRequest,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
